/**
 * レポートサービス
 *
 * バックエンドAPI呼び出し:
 * - GET /api/projects/:id/report - レポートデータ取得
 * - POST /api/projects/:id/report/pdf - PDFレポート生成
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type { Project, RiskCategory } from '../../types';

// ========================================
// 型定義
// ========================================

/** レポートフォーマット */
export type ReportFormat = 'pdf' | 'excel';

/** リスクカテゴリサマリー */
export interface RiskCategorySummary {
  category: RiskCategory;
  categoryName: string;
  total: number;
  completed: number;
  completionRate: number;
}

/** チェック結果サマリー */
export interface CheckResultSummary {
  total: number;
  completed: number;
  unchecked: number;
  notApplicable: number;
  needsReview: number;
}

/** 必要書類 */
export interface RequiredDocument {
  name: string;
  status: 'prepared' | 'not_prepared';
}

/** 手続きステップ */
export interface ProcedureStep {
  name: string;
  status: 'completed' | 'in_progress' | 'not_started';
}

/** レポートデータ */
export interface ReportData {
  project: Project;
  checkResultSummary: CheckResultSummary;
  riskCategorySummary: RiskCategorySummary[];
  requiredDocuments: RequiredDocument[];
  procedureSteps: ProcedureStep[];
  generatedAt: string;
}

/** PDFレポート生成リクエスト */
export interface GenerateReportRequest {
  projectId: string;
  format: ReportFormat;
  includeDetails?: boolean;
}

// ========================================
// バックエンドレスポンス型（snake_case）
// ========================================

interface BackendCheckResultSummary {
  total: number;
  completed: number;
  unchecked: number;
  not_applicable: number;
  needs_review: number;
}

interface BackendRiskCategorySummary {
  category: RiskCategory;
  category_name: string;
  total: number;
  completed: number;
  completion_rate: number;
}

interface BackendRequiredDocument {
  name: string;
  status: 'prepared' | 'not_prepared';
}

interface BackendProcedureStep {
  name: string;
  status: 'completed' | 'in_progress' | 'not_started';
}

interface BackendProjectResponse {
  id: string;
  name: string;
  ma_method: string;
  target_company: string;
  target_country: string;
  status: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface BackendReportResponse {
  project: BackendProjectResponse;
  check_result_summary: BackendCheckResultSummary;
  risk_category_summary: BackendRiskCategorySummary[];
  required_documents: BackendRequiredDocument[];
  procedure_steps: BackendProcedureStep[];
  generated_at: string;
}

// ========================================
// 変換関数
// ========================================

function convertCheckResultSummary(summary: BackendCheckResultSummary): CheckResultSummary {
  return {
    total: summary.total,
    completed: summary.completed,
    unchecked: summary.unchecked,
    notApplicable: summary.not_applicable,
    needsReview: summary.needs_review,
  };
}

function convertRiskCategorySummary(summary: BackendRiskCategorySummary): RiskCategorySummary {
  return {
    category: summary.category,
    categoryName: summary.category_name,
    total: summary.total,
    completed: summary.completed,
    completionRate: summary.completion_rate,
  };
}

function convertProject(p: BackendProjectResponse): Project {
  return {
    id: p.id,
    name: p.name,
    maMethod: p.ma_method as Project['maMethod'],
    targetCompany: p.target_company,
    targetCountry: p.target_country as Project['targetCountry'],
    status: p.status as Project['status'],
    description: p.description,
    createdBy: p.created_by,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  };
}

function convertReportData(data: BackendReportResponse): ReportData {
  return {
    project: convertProject(data.project),
    checkResultSummary: convertCheckResultSummary(data.check_result_summary),
    riskCategorySummary: data.risk_category_summary.map(convertRiskCategorySummary),
    requiredDocuments: data.required_documents,
    procedureSteps: data.procedure_steps,
    generatedAt: data.generated_at,
  };
}

// ========================================
// サービス
// ========================================

export const reportService = {
  /**
   * レポートデータ取得
   * GET /api/projects/:id/report
   */
  async getReportData(projectId: string): Promise<ReportData> {
    try {
      const response = await apiClient.get<BackendReportResponse>(
        `/api/projects/${projectId}/report`
      );
      return convertReportData(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * PDFレポート生成
   * POST /api/projects/:id/report/pdf
   * @returns Blob形式のPDFデータ
   */
  async generatePdfReport(projectId: string, includeDetails?: boolean): Promise<Blob> {
    try {
      const response = await apiClient.post(
        `/api/projects/${projectId}/report/pdf`,
        { include_details: includeDetails ?? true },
        { responseType: 'blob' }
      );
      return response.data as Blob;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * レポートダウンロードヘルパー
   * BlobをダウンロードリンクとしてDOMに追加し、自動クリックでダウンロード開始
   */
  downloadReport(blob: Blob, filename: string, format: ReportFormat = 'pdf'): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const extension = format === 'pdf' ? 'pdf' : 'xlsx';
    link.setAttribute('download', `${filename}.${extension}`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default reportService;
